<template>
  <div class="embryo-collection py-2">
    <div class="embryo-rig p-3 p-lg-4">
      <div class="embryo-rig__grid">
        <section
          ref="stageRegion"
          class="embryo-stage p-3"
          aria-label="Sow oocyte workspace"
          tabindex="-1"
        >
          <div class="embryo-stage__workspace">
            <div
              class="embryo-stage__feedback"
              :class="[
                guideMode === 'keyboard'
                  ? 'embryo-stage__feedback--keyboard'
                  : 'embryo-stage__feedback--mouse',
                guideMode === 'keyboard' && isOocyteNearby
                  ? 'embryo-stage__feedback--keyboard-near'
                  : ''
              ]"
              :title="stageFeedbackText"
            >
              <div class="embryo-stage__feedback-label">
                {{
                  guideMode === "keyboard" ? "Keyboard feedback" : "Mouse tip"
                }}
              </div>
              <p class="embryo-stage__feedback-text mb-0">
                {{ stageFeedbackText }}
              </p>
            </div>

            <div class="embryo-stage__scope-wrap">
              <SowOocyteSketch
                :key="sketchKey"
                :cpu-target="cpuTarget"
                :adaptive-quality="adaptiveQuality"
                :is-primed="isPipettePrimed"
                :guided-hotspot-id="guidedHotspotId"
                :show-guided-outline="showKeyboardGuidedOutline"
                ref="sketch"
                @specimen-collected="onSpecimenCollected"
                @all-collected="onAllCollected"
                @interaction="onInteraction"
                @blocked="onBlocked"
                @needs-prime="onNeedsPrime"
              />
            </div>

            <div class="mt-2 mt-lg-3">
              <div class="row g-2">
                <div class="col-6">
                  <button
                    class="btn btn-lg w-100 embryo-stage__action-btn"
                    type="button"
                    @click="shakeDish"
                  >
                    <span class="bi bi-shuffle me-2" aria-hidden="true"></span>
                    Shake Dish
                  </button>
                </div>
                <div class="col-6">
                  <button
                    :class="[
                      'btn btn-lg w-100',
                      isPipettePrimed
                        ? 'embryo-stage__action-btn'
                        : 'embryo-stage__action-btn-outline'
                    ]"
                    type="button"
                    title="Prime before collecting each oocyte. Priming does not affect debris movement."
                    @click="primePipette"
                  >
                    <span
                      class="bi bi-droplet-half me-2"
                      aria-hidden="true"
                    ></span>
                    {{ isPipettePrimed ? "Pipette Primed" : "Prime Pipette" }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          class="embryo-status d-flex flex-column"
          aria-label="Collected sow oocytes"
        >
          <div class="embryo-status__body p-3">
            <div class="embryo-progress card border-0 shadow-sm mb-3">
              <div class="card-body p-3">
                <h2 class="embryo-status__title mb-2">Oocytes Collected</h2>

                <div class="embryo-tray mb-3">
                  <div
                    v-for="n in 5"
                    :key="n"
                    class="embryo-tray__slot"
                    :class="{
                      'embryo-tray__slot--filled': n <= collectedCount,
                      'embryo-tray__slot--animating': n === collectedCount
                    }"
                    :aria-label="`Collection slot ${n}: ${n <= collectedCount ? 'sow oocyte collected' : 'empty'}`"
                    role="img"
                  >
                    <div v-if="n <= collectedCount" class="embryo-tray__embryo">
                      <div class="embryo-tray__outer-shell"></div>
                      <div class="embryo-tray__inner-core">
                        <div class="embryo-tray__cap"></div>
                      </div>
                    </div>
                  </div>
                </div>

                <p class="text-center mb-0">
                  <span class="fw-bold fs-3">{{ collectedCount }}</span>
                  <span class="text-body-secondary"> / 5 collected</span>
                </p>
              </div>
            </div>

            <div class="embryo-guide card border-0 shadow-sm">
              <div class="card-body p-2 p-lg-3 text-start embryo-guide__body">
                <h3 class="h6 mb-1">Choose Your Collection Approach</h3>
                <p class="small text-body-secondary mb-2 fw-semibold">
                  Start with one approach and switch anytime
                </p>

                <div
                  class="btn-group embryo-guide__tabs w-100 mb-2"
                  role="group"
                  aria-label="Collection approach"
                >
                  <button
                    id="guide-tab-mouse"
                    class="btn btn-sm"
                    :class="
                      guideMode === 'mouse'
                        ? 'btn-primary'
                        : 'btn-outline-primary'
                    "
                    type="button"
                    :aria-pressed="guideMode === 'mouse'"
                    @click="setGuideMode('mouse')"
                  >
                    Mouse
                  </button>
                  <button
                    id="guide-tab-keyboard"
                    class="btn btn-sm"
                    :class="
                      guideMode === 'keyboard'
                        ? 'btn-primary'
                        : 'btn-outline-primary'
                    "
                    type="button"
                    :aria-pressed="guideMode === 'keyboard'"
                    @click="setGuideMode('keyboard')"
                  >
                    Keyboard
                  </button>
                </div>

                <div
                  v-if="guideMode === 'mouse'"
                  id="guide-panel-mouse"
                  role="region"
                  aria-label="Mouse approach details"
                >
                  <p class="small text-body-secondary mb-2">
                    Mouse-Driven: click and drag debris in the dish to clear a
                    path, then collect exposed oocytes.
                  </p>
                  <h4 class="h6 small text-uppercase mb-1">Mouse workflow</h4>
                  <ol class="small text-body-secondary ps-3 mb-2">
                    <li>
                      Click or click-and-drag debris, bubbles, and particles to
                      clear an area.
                    </li>
                    <li>
                      Click <strong>Prime Pipette</strong> before each
                      collection attempt.
                    </li>
                    <li>Click a fully exposed oocyte to collect it.</li>
                  </ol>
                  <p class="small text-body-secondary mb-0">
                    Guided target highlighting is off in this mode.
                  </p>
                </div>

                <div
                  v-else
                  id="guide-panel-keyboard"
                  role="region"
                  aria-label="Keyboard approach details"
                >
                  <p class="small text-body-secondary mb-2">
                    Keyboard mode: a starting target is selected in the dish.
                  </p>
                  <label
                    class="form-label small mb-1"
                    for="search-pattern-select"
                  >
                    Scan pattern
                  </label>
                  <select
                    id="search-pattern-select"
                    class="form-select form-select-sm"
                    :value="searchPattern"
                    @change="onPatternChanged"
                  >
                    <option value="expanding-square">Expanding Square</option>
                    <option value="sector-scan">Sector Scan</option>
                  </select>

                  <div class="mt-2 small" aria-label="Keyboard shortcut legend">
                    <div class="row g-1 mb-1">
                      <div class="col-6 text-body-secondary">
                        <span class="badge text-bg-light border text-dark me-1"
                          >D</span
                        >
                        Next target
                      </div>
                      <div class="col-6 text-body-secondary">
                        <span class="badge text-bg-light border text-dark me-1"
                          >A</span
                        >
                        Previous target
                      </div>
                    </div>
                    <div class="row g-1 mb-1">
                      <div class="col-6 text-body-secondary">
                        <span class="badge text-bg-light border text-dark me-1"
                          >W</span
                        >
                        Collect check
                      </div>
                      <div class="col-6 text-body-secondary">
                        <span class="badge text-bg-light border text-dark me-1"
                          >S</span
                        >
                        Nudge debris
                      </div>
                    </div>
                    <div class="row g-1">
                      <div class="col-6 text-body-secondary">
                        <span class="badge text-bg-light border text-dark me-1"
                          >P</span
                        >
                        Prime pipette
                      </div>
                      <div class="col-6 text-body-secondary">
                        <span class="badge text-bg-light border text-dark me-1"
                          >E / F</span
                        >
                        Hint or focus
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="embryo-status__bottom mt-auto">
              <div
                :class="[
                  'embryo-side-tools',
                  !isDebugMode ? 'embryo-side-tools--two' : ''
                ]"
                role="group"
                aria-label="Activity controls"
              >
                <button
                  class="btn btn-light embryo-side-tools__btn"
                  type="button"
                  :aria-expanded="showInstructions.toString()"
                  aria-controls="embryo-instructions"
                  @click="showInstructions = !showInstructions"
                >
                  <span
                    class="bi bi-info-circle me-2"
                    aria-hidden="true"
                  ></span>
                  {{
                    showInstructions
                      ? "Hide Detailed Instructions"
                      : "Show Detailed Instructions"
                  }}
                </button>
                <button
                  :class="[
                    'btn embryo-side-tools__btn',
                    completed
                      ? 'btn-success embryo-side-tools__reset-ready'
                      : 'btn-light'
                  ]"
                  type="button"
                  @click="resetActivity"
                >
                  <span
                    class="bi bi-arrow-counterclockwise me-2"
                    aria-hidden="true"
                  ></span>
                  {{ completed ? "Try Again" : "Reset Activity" }}
                </button>
                <button
                  v-if="isDebugMode"
                  ref="performanceSettingsTrigger"
                  class="btn btn-outline-dark embryo-side-tools__btn"
                  type="button"
                  aria-haspopup="dialog"
                  aria-controls="performance-settings-modal"
                  :aria-expanded="showPerformanceSettings.toString()"
                  @click="openPerformanceSettings"
                >
                  <span class="bi bi-sliders me-2" aria-hidden="true"></span>
                  Performance Settings
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div
        v-if="isDebugMode && showPerformanceSettings"
        id="performance-settings-modal"
        ref="performanceSettingsModal"
        class="modal d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="performance-settings-title"
        @click.self="closePerformanceSettings"
      >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h2 id="performance-settings-title" class="modal-title h5 mb-0">
                Performance Settings
              </h2>
              <button
                ref="performanceSettingsCloseBtn"
                type="button"
                class="btn-close"
                aria-label="Close"
                @click="closePerformanceSettings"
              ></button>
            </div>

            <div class="modal-body text-start">
              <p class="mb-2">
                These controls tune rendering behavior for testing and
                lower-power devices.
              </p>
              <ul class="small text-body-secondary mb-3">
                <li>
                  <strong>CPU Target</strong> adds a controlled workload to help
                  simulate slower hardware.
                </li>
                <li>
                  <strong>Auto Quality</strong> adjusts visual fidelity
                  automatically to keep interaction smooth.
                </li>
              </ul>

              <div class="d-grid gap-2">
                <button
                  class="btn btn-outline-primary"
                  type="button"
                  @click="cycleCpuTarget"
                >
                  <span class="bi bi-cpu me-2" aria-hidden="true"></span>
                  CPU {{ cpuTarget }}%
                </button>
                <button
                  class="btn btn-outline-primary"
                  type="button"
                  @click="toggleAdaptiveQuality"
                >
                  <span
                    class="bi bi-speedometer2 me-2"
                    aria-hidden="true"
                  ></span>
                  Auto {{ adaptiveQuality ? "On" : "Off" }}
                </button>
              </div>
            </div>

            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-secondary"
                @click="closePerformanceSettings"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="isDebugMode && showPerformanceSettings"
        class="modal-backdrop fade show"
      ></div>

      <div class="mt-3">
        <div
          v-if="blockedMessage"
          class="alert alert-warning py-2 px-3 small mb-2"
          role="alert"
        >
          <span
            class="bi bi-exclamation-triangle me-1"
            aria-hidden="true"
          ></span>
          Oocyte is still partially covered - move the debris first!
        </div>

        <div
          v-if="needsPrimeMessage"
          class="alert alert-warning py-2 px-3 small mb-2"
          role="alert"
        >
          <span class="bi bi-droplet-half me-1" aria-hidden="true"></span>
          Prime the pipette before collecting an oocyte.
        </div>

        <div v-if="completed" class="alert alert-primary border-2 mb-0">
          <span class="bi bi-check-circle-fill me-2" aria-hidden="true"></span>
          All 5 unfertilized sow oocytes collected. Great work.
        </div>
      </div>

      <div
        id="embryo-instructions"
        v-show="showInstructions"
        class="embryo-instructions mt-3"
      >
        <CustomAlert
          title="Detailed Instructions: How to Collect Unfertilized Oocytes"
          type="primary"
          icon="bi-eyedropper"
        >
          <p>
            You are looking through a virtual dissection scope at a dish of
            <strong>unfertilized sow oocytes</strong> hidden among debris.
          </p>
          <ol class="mb-2">
            <li>
              <strong>Move debris:</strong> Click or click-and-drag a debris
              fragment, bubble, or particle to push it aside with the pipette.
            </li>
            <li>
              <strong>Prime pipette:</strong> Click
              <strong>Prime Pipette</strong>
              before each oocyte collection attempt.
            </li>
            <li>
              <strong>Collect oocytes:</strong> Once an oocyte is fully exposed,
              click it to move it into the collection dish on the right.
            </li>
            <li>
              Collect all <strong>5 unfertilized oocytes</strong> to complete
              the activity.
            </li>
          </ol>
          <div class="embryo-keyboard-guidance row g-2 mb-3">
            <div class="col-12">
              <div class="embryo-keyboard-guidance__panel h-100">
                <h3 class="h6 mb-2">Reliable keyboard loop</h3>
                <ul
                  class="list-unstyled mb-0 small embryo-keyboard-guidance__list"
                >
                  <li
                    class="d-flex justify-content-between align-items-start gap-2"
                  >
                    <span
                      ><strong>Step 1:</strong> focus the first search item in
                      the dish.</span
                    >
                    <span
                      class="d-flex gap-1 flex-shrink-0 embryo-keyboard-guidance__keys"
                    >
                      <span class="fw-semibold">Key</span>
                      <kbd>F</kbd>
                    </span>
                  </li>
                  <li
                    class="d-flex justify-content-between align-items-start gap-2"
                  >
                    <span
                      ><strong>Step 2:</strong> check this area for an oocyte,
                      then move to the next item if needed.</span
                    >
                    <span
                      class="d-flex gap-1 flex-shrink-0 embryo-keyboard-guidance__keys"
                    >
                      <span class="fw-semibold">Keys</span>
                      <kbd>D</kbd>
                      <kbd>W</kbd>
                    </span>
                  </li>
                  <li
                    class="d-flex justify-content-between align-items-start gap-2"
                  >
                    <span
                      ><strong>When oocyte is nearby:</strong> prime first, then
                      work in the same small area.</span
                    >
                    <span
                      class="d-flex gap-1 flex-shrink-0 embryo-keyboard-guidance__keys"
                    >
                      <span class="fw-semibold">Keys</span>
                      <kbd>P</kbd>
                      <kbd>S</kbd>
                      <kbd>W</kbd>
                    </span>
                  </li>
                  <li
                    class="d-flex justify-content-between align-items-start gap-2"
                  >
                    <span
                      ><strong>After success:</strong> return to search mode,
                      then repeat from the next item.</span
                    >
                    <span
                      class="d-flex gap-1 flex-shrink-0 embryo-keyboard-guidance__keys"
                    >
                      <span class="fw-semibold">Keys</span>
                      <kbd>D</kbd>
                      <kbd>W</kbd>
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div class="embryo-key-legend mt-3">
            <h3 class="h6 mb-2">Keyboard Legend</h3>
            <ul class="row g-2 list-unstyled mb-0 small">
              <li class="col-6 col-lg-3">
                <div
                  class="d-flex justify-content-between align-items-center border rounded bg-white px-2 py-1"
                >
                  <span>Next search target</span>
                  <kbd>D</kbd>
                </div>
              </li>
              <li class="col-6 col-lg-3">
                <div
                  class="d-flex justify-content-between align-items-center border rounded bg-white px-2 py-1"
                >
                  <span>Previous search target</span>
                  <kbd>A</kbd>
                </div>
              </li>
              <li class="col-6 col-lg-3">
                <div
                  class="d-flex justify-content-between align-items-center border rounded bg-white px-2 py-1"
                >
                  <span>Nudge debris</span>
                  <kbd>S</kbd>
                </div>
              </li>
              <li class="col-6 col-lg-3">
                <div
                  class="d-flex justify-content-between align-items-center border rounded bg-white px-2 py-1"
                >
                  <span>Collect attempt</span>
                  <kbd>W</kbd>
                </div>
              </li>
              <li class="col-6 col-lg-3">
                <div
                  class="d-flex justify-content-between align-items-center border rounded bg-white px-2 py-1"
                >
                  <span>Prime pipette</span>
                  <kbd>P</kbd>
                </div>
              </li>
              <li class="col-6 col-lg-3">
                <div
                  class="d-flex justify-content-between align-items-center border rounded bg-white px-2 py-1"
                >
                  <span>Shake dish</span>
                  <kbd>Q</kbd>
                </div>
              </li>
              <li class="col-6 col-lg-3">
                <div
                  class="d-flex justify-content-between align-items-center border rounded bg-white px-2 py-1"
                >
                  <span>Repeat hint</span>
                  <kbd>E</kbd>
                </div>
              </li>
              <li class="col-6 col-lg-3">
                <div
                  class="d-flex justify-content-between align-items-center border rounded bg-white px-2 py-1"
                >
                  <span>Focus first search item</span>
                  <kbd>F</kbd>
                </div>
              </li>
            </ul>
          </div>
        </CustomAlert>
      </div>
    </div>

    <div aria-live="polite" aria-atomic="true" class="visually-hidden">
      {{ liveRegionText }}
    </div>
    <div aria-live="assertive" aria-atomic="true" class="visually-hidden">
      {{ warningLiveRegionText }}
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import DatastoreAPI from "@/helpers/DatastoreAPI";
import SowOocyteSketch from "@/components/activity/EmbryoSketch.vue";
import { seedRNG } from "@/helpers/random";
import config from "@/data/config.json";

export default {
  name: "SowOocyteCollection",

  components: {
    SowOocyteSketch
  },

  mixins: [DatastoreAPI],

  data() {
    return {
      sketchKey: 0,
      collectedCount: 0,
      totalInteractions: 0,
      completed: false,
      cpuTarget: 0,
      adaptiveQuality: true,
      isPipettePrimed: false,
      showInstructions: false,
      liveRegionText: "",
      warningLiveRegionText: "",
      searchPattern: "expanding-square",
      guideMode: "mouse",
      showPerformanceSettings: false,
      guidedHotspotId: "",
      targetedEmbryoId: "",
      guidedTargetOrderIds: [],
      guidedTargetOrderIndex: -1,
      showKeyboardGuidedOutline: false,
      guidedSummary:
        "Press D to cycle through items, then press W to check for oocytes.",
      blockedMessage: false,
      blockedTimer: null,
      needsPrimeMessage: false,
      needsPrimeTimer: null,
      srInterruptTimer: null,
      srWarningInterruptTimer: null,
      shortcutAnnouncementContext: {
        text: "",
        at: 0
      }
    };
  },

  computed: {
    ...mapGetters(["getSowOocyteCollection", "mapToDatastore", "isDebugMode"]),

    isOocyteNearby() {
      return !!this.targetedEmbryoId;
    },

    stageFeedbackText() {
      if (this.guideMode === "keyboard") {
        return (
          this.guidedSummary ||
          "Press D to cycle through items, then press W to check for oocytes."
        );
      }

      return "Mouse mode active. Click or drag debris, then prime and collect oocytes.";
    }
  },

  mounted() {
    if (this.getSowOocyteCollection?.completed) {
      this.completed = true;
      this.collectedCount = 5;
      this.liveRegionText =
        "Sow unfertilized oocyte collection already completed.";
      this.guidedSummary = "Activity already completed.";
      return;
    }

    if (this.guideMode === "keyboard") {
      this.initializeGuidedSearch();
    } else {
      this.setGuideMode("mouse");
    }

    window.addEventListener("keydown", this.onShortcutKeydown);
    window.addEventListener("mousedown", this.onMouseInput, true);
  },

  beforeUnmount() {
    window.removeEventListener("keydown", this.onShortcutKeydown);
    window.removeEventListener("mousedown", this.onMouseInput, true);
    clearTimeout(this.blockedTimer);
    clearTimeout(this.needsPrimeTimer);
    clearTimeout(this.srInterruptTimer);
    clearTimeout(this.srWarningInterruptTimer);
  },

  methods: {
    ...mapActions(["setSowOocyteCollection"]),

    shouldHandleShortcut(event) {
      if (event.defaultPrevented) return false;
      if (event.altKey || event.ctrlKey || event.metaKey) return false;

      const tagName = (event.target?.tagName || "").toUpperCase();
      if (
        tagName === "INPUT" ||
        tagName === "TEXTAREA" ||
        tagName === "SELECT"
      ) {
        return false;
      }
      if (event.target?.isContentEditable) return false;

      return true;
    },

    onMouseInput(event) {
      const targetEl = event?.target;
      if (!(targetEl instanceof Element)) return;
      if (!targetEl.closest(".embryo-stage")) return;

      this.showKeyboardGuidedOutline = false;

      if (this.guideMode !== "keyboard") return;

      this.setGuideMode("mouse");
      this.announceLiveRegion("Switched to mouse-driven mode.", false);
    },

    setGuideMode(mode) {
      if (mode !== "mouse" && mode !== "keyboard") return;
      this.guideMode = mode;

      if (mode === "mouse") {
        this.guidedHotspotId = "";
        this.targetedEmbryoId = "";
        this.guidedTargetOrderIds = [];
        this.guidedTargetOrderIndex = -1;
        this.showKeyboardGuidedOutline = false;
        this.guidedSummary =
          "Mouse mode active. Guided target highlighting is off.";
        if (
          this.$refs.sketch &&
          typeof this.$refs.sketch.clearHotspotFocus === "function"
        ) {
          this.$refs.sketch.clearHotspotFocus();
        }
        return;
      }

      this.showKeyboardGuidedOutline = true;
      this.activateKeyboardGuideStart();
      this.focusStageWorkspace();
    },

    focusStageWorkspace() {
      this.$nextTick(() => {
        if (
          this.$refs.stageRegion &&
          typeof this.$refs.stageRegion.focus === "function"
        ) {
          this.$refs.stageRegion.focus({ preventScroll: true });
        }
      });
    },

    activateKeyboardGuideStart() {
      const targets = this.getNavigableTargets();
      if (!targets.length) {
        this.guidedHotspotId = "";
        this.guidedTargetOrderIds = [];
        this.guidedTargetOrderIndex = -1;
        this.guidedSummary = "No guided targets are available right now.";
        return;
      }

      this.syncGuidedTargetOrder(targets);

      const hasCurrent = targets.some(
        (target) => target.id === this.guidedHotspotId
      );
      const startingTarget = hasCurrent
        ? targets.find((t) => t.id === this.guidedHotspotId)
        : targets[0];

      this.guidedHotspotId = startingTarget.id;
      this.guidedTargetOrderIndex = this.guidedTargetOrderIds.indexOf(
        startingTarget.id
      );
      this.guidedSummary =
        "Press D to cycle through items, then press W to check for oocytes.";
      this.$refs.sketch.focusHotspot(startingTarget.id);
    },

    onShortcutKeydown(event) {
      if (this.completed) return;
      if (!this.shouldHandleShortcut(event)) return;

      if (this.guideMode !== "keyboard") {
        this.setGuideMode("keyboard");
      }

      const key = (event.key || "").toLowerCase();
      if (key === "d") {
        event.preventDefault();
        this.showKeyboardGuidedOutline = true;
        this.setShortcutAnnouncementContext(key);
        this.focusNextGuidedTarget(true, true);
        return;
      }

      if (key === "a") {
        event.preventDefault();
        this.showKeyboardGuidedOutline = true;
        this.setShortcutAnnouncementContext(key);
        this.focusPreviousGuidedTarget();
        return;
      }

      if (key === "s") {
        event.preventDefault();
        this.showKeyboardGuidedOutline = true;
        this.setShortcutAnnouncementContext(key);
        this.nudgeGuidedTarget();
        return;
      }

      if (key === "q") {
        event.preventDefault();
        this.showKeyboardGuidedOutline = true;
        this.setShortcutAnnouncementContext(key);
        this.shakeDish();
        return;
      }

      if (key === "w") {
        event.preventDefault();
        this.showKeyboardGuidedOutline = true;
        this.setShortcutAnnouncementContext(key);
        this.collectGuidedTarget();
        return;
      }

      if (key === "e") {
        event.preventDefault();
        this.showKeyboardGuidedOutline = true;
        this.setShortcutAnnouncementContext(key);
        this.repeatGuidedHint();
        return;
      }

      if (key === "f") {
        event.preventDefault();
        this.showKeyboardGuidedOutline = true;
        this.setShortcutAnnouncementContext(key);
        this.focusCurrentTarget();
        return;
      }

      if (key === "p") {
        event.preventDefault();
        this.showKeyboardGuidedOutline = true;
        this.setShortcutAnnouncementContext(key);
        this.primePipette();
        return;
      }

      if (key === "escape" && this.showPerformanceSettings) {
        event.preventDefault();
        this.showKeyboardGuidedOutline = true;
        this.setShortcutAnnouncementContext(key);
        this.closePerformanceSettings();
      }
    },

    setShortcutAnnouncementContext(key) {
      const keyLabelMap = {
        a: "A previous target",
        d: "D next target",
        e: "E repeat hint",
        escape: "Escape close modal",
        f: "F focus first target",
        p: "P prime pipette",
        q: "Q shake dish",
        s: "S nudge debris",
        w: "W collect attempt"
      };

      const text = keyLabelMap[key] || `${key.toUpperCase()} action`;
      this.shortcutAnnouncementContext = {
        text,
        at: Date.now()
      };
    },

    getActiveShortcutAnnouncementPrefix() {
      const maxAgeMs = 2000;
      const age = Date.now() - this.shortcutAnnouncementContext.at;
      if (!this.shortcutAnnouncementContext.text || age > maxAgeMs) {
        return "";
      }
      return `${this.shortcutAnnouncementContext.text}:`;
    },

    announceLiveRegion(
      message,
      includeShortcutPrefix = true,
      urgency = "polite"
    ) {
      const prefix = includeShortcutPrefix
        ? this.getActiveShortcutAnnouncementPrefix()
        : "";
      const nextMessage = prefix ? `${prefix} ${message}` : message;

      if (urgency === "assertive") {
        clearTimeout(this.srWarningInterruptTimer);
        this.warningLiveRegionText = "";
        this.srWarningInterruptTimer = setTimeout(() => {
          this.warningLiveRegionText = nextMessage;
        }, 24);
        return;
      }

      clearTimeout(this.srInterruptTimer);
      this.liveRegionText = "";
      this.srInterruptTimer = setTimeout(() => {
        this.liveRegionText = nextMessage;
      }, 24);
    },

    getCurrentGuidedTarget() {
      const allTargets = this.getGuidedTargets();
      if (!allTargets.length) return null;

      const current = allTargets.find((t) => t.id === this.guidedHotspotId);
      if (current) {
        return current;
      }

      const targets = this.getNavigableTargets();
      if (!targets.length) return null;

      const fallback = targets[0];
      this.guidedHotspotId = fallback.id;
      this.$refs.sketch.focusHotspot(fallback.id);
      return fallback;
    },

    getNavigableTargets() {
      return this.getGuidedTargets().filter(
        (target) => target.type !== "embryo"
      );
    },

    syncGuidedTargetOrder(targetsInput = null) {
      const targets = targetsInput || this.getNavigableTargets();
      const ids = targets.map((target) => target.id);
      if (!ids.length) {
        this.guidedTargetOrderIds = [];
        this.guidedTargetOrderIndex = -1;
        return;
      }

      if (!this.guidedTargetOrderIds.length) {
        this.guidedTargetOrderIds = ids;
      } else {
        const idSet = new Set(ids);
        const keptIds = this.guidedTargetOrderIds.filter((id) => idSet.has(id));
        const appendedIds = ids.filter((id) => !keptIds.includes(id));
        this.guidedTargetOrderIds = [...keptIds, ...appendedIds];
      }

      if (this.guidedHotspotId) {
        const currentIndex = this.guidedTargetOrderIds.indexOf(
          this.guidedHotspotId
        );
        if (currentIndex >= 0) {
          this.guidedTargetOrderIndex = currentIndex;
          return;
        }
      }

      if (
        this.guidedTargetOrderIndex < 0 ||
        this.guidedTargetOrderIndex >= this.guidedTargetOrderIds.length
      ) {
        this.guidedTargetOrderIndex = 0;
      }
    },

    nudgeGuidedTarget() {
      const target = this.getCurrentGuidedTarget();
      if (!target) {
        this.announceLiveRegion("No target available to nudge.");
        return;
      }

      const canNudgeArea =
        this.$refs.sketch &&
        typeof this.$refs.sketch.nudgeDebrisInArea === "function";

      const areaCenterId = this.targetedEmbryoId || target.id;
      const useEmbryoRadius =
        Boolean(this.targetedEmbryoId) || target.type === "embryo";
      const areaRadius = useEmbryoRadius ? 72 : 56;

      if (canNudgeArea) {
        const moved = this.$refs.sketch.nudgeDebrisInArea(
          areaCenterId,
          areaRadius
        );
        if (this.targetedEmbryoId) {
          this.guidedHotspotId = this.targetedEmbryoId;
          this.$refs.sketch.focusHotspot(this.targetedEmbryoId);
        }

        if (!moved) {
          this.announceLiveRegion(
            this.targetedEmbryoId
              ? "No debris left in this focused oocyte area. Press W to collect."
              : "No debris in this focused area. Press D for next target or W to check here."
          );
          this.guidedSummary = this.targetedEmbryoId
            ? "Area looks clear. Press W to collect the oocyte."
            : "Area has little debris movement left. Press D then W to continue searching.";
          return;
        }

        this.announceLiveRegion(
          this.targetedEmbryoId
            ? `Nudged ${moved} debris pieces near the oocyte. Press W to retry collection.`
            : `Nudged ${moved} debris pieces in the focused area.`
        );
        this.guidedSummary = this.targetedEmbryoId
          ? "Debris shifted near oocyte. Press W to check collection, then S again if needed."
          : "Debris shifted in this area. Press W to check for a nearby oocyte, or D for next.";
        return;
      }

      if (this.targetedEmbryoId) {
        const blocker =
          this.$refs.sketch &&
          typeof this.$refs.sketch.getUnblockingTarget === "function"
            ? this.$refs.sketch.getUnblockingTarget(this.targetedEmbryoId)
            : null;

        if (!blocker) {
          this.guidedHotspotId = this.targetedEmbryoId;
          this.$refs.sketch.focusHotspot(this.targetedEmbryoId);
          this.announceLiveRegion(
            "No blocking debris near the targeted oocyte. Press W to collect."
          );
          this.guidedSummary =
            "Targeted oocyte appears clear. Press W to collect.";
          return;
        }

        this.guidedHotspotId = blocker.id;
        this.$refs.sketch.interactWithId(blocker.id);
        this.announceLiveRegion(
          "Nudged blocking debris near the targeted oocyte."
        );
        this.guidedSummary =
          "Debris moved near targeted oocyte. Press W to retry collection.";
        return;
      }

      if (target.type !== "embryo") {
        this.$refs.sketch.interactWithId(target.id);
        this.announceLiveRegion(`Nudged ${target.type}.`);
        this.guidedSummary = `Nudged ${target.type}. Press D for next target.`;
        return;
      }

      const blocker =
        this.$refs.sketch &&
        typeof this.$refs.sketch.getUnblockingTarget === "function"
          ? this.$refs.sketch.getUnblockingTarget(target.id)
          : null;

      if (!blocker) {
        this.announceLiveRegion(
          "No blocking debris near this oocyte. Prime with P and collect with W."
        );
        this.guidedSummary =
          "No blocker near this oocyte. Prime with P and collect with W.";
        return;
      }

      this.$refs.sketch.interactWithId(blocker.id);
      this.$refs.sketch.focusHotspot(target.id);
      this.announceLiveRegion(
        "Nudged blocking debris near the selected oocyte."
      );
      this.guidedSummary =
        "Blocking debris nudged. Press W to retry collection or S to nudge again.";
    },

    collectGuidedTarget() {
      const target = this.getCurrentGuidedTarget();
      if (!target) {
        this.announceLiveRegion("No target available to collect.");
        return;
      }

      if (
        !this.$refs.sketch ||
        typeof this.$refs.sketch.interactWithId !== "function"
      ) {
        this.announceLiveRegion("Collection helper is unavailable.");
        return;
      }

      if (target.type === "embryo") {
        this.targetedEmbryoId = target.id;
        this.$nextTick(() => {
          if (
            this.$refs.sketch &&
            typeof this.$refs.sketch.interactWithId === "function"
          ) {
            this.$refs.sketch.interactWithId(target.id);
          }
        });
        return;
      }

      if (this.targetedEmbryoId) {
        this.guidedHotspotId = this.targetedEmbryoId;
        this.$refs.sketch.focusHotspot(this.targetedEmbryoId);
        this.$nextTick(() => {
          if (
            this.$refs.sketch &&
            typeof this.$refs.sketch.interactWithId === "function"
          ) {
            this.$refs.sketch.interactWithId(this.targetedEmbryoId);
          }
        });
        return;
      }

      if (
        typeof this.$refs.sketch.evaluateCollectionFromTarget !== "function"
      ) {
        this.announceLiveRegion("Collection helper is unavailable.");
        return;
      }

      const result = this.$refs.sketch.evaluateCollectionFromTarget(
        target.id,
        42
      );

      if (result.state === "none") {
        this.announceLiveRegion(
          "No oocyte near this item. Press D for the next item, then W to check again."
        );
        this.guidedSummary =
          "No oocyte near this item. Press D then W to keep searching.";
        return;
      }

      this.targetedEmbryoId = result.embryoId;

      if (result.state === "blocked") {
        this.announceLiveRegion(
          "Oocyte nearby. Press P to prime, then use S and W in this small area.",
          true,
          "assertive"
        );
        this.guidedSummary =
          "Oocyte nearby. Press P to prime, then alternate S and W to collect.";

        if (result.blockerId) {
          this.guidedHotspotId = result.blockerId;
          this.$refs.sketch.focusHotspot(result.blockerId);
        }
        return;
      }

      if (result.state === "unprimed") {
        this.guidedHotspotId = result.embryoId;
        this.$refs.sketch.focusHotspot(result.embryoId);
        this.onNeedsPrime();
        this.guidedSummary =
          "Oocyte nearby. Press P, then use S and W until collected.";
        return;
      }

      this.guidedHotspotId = result.embryoId;
      this.$refs.sketch.focusHotspot(result.embryoId);
      this.announceLiveRegion(
        "Oocyte nearby and in focus. Press P, then use S and W until collected.",
        true,
        "assertive"
      );
      this.guidedSummary =
        "Oocyte nearby. Press P, then alternate S and W until collected.";
    },

    primePipette() {
      this.isPipettePrimed = true;
      this.needsPrimeMessage = false;
      clearTimeout(this.needsPrimeTimer);
      this.announceLiveRegion("Pipette primed and ready for collection.");
      this.guidedSummary = this.targetedEmbryoId
        ? "Pipette primed. Press W to collect; if still blocked, alternate S and W."
        : "Pipette primed. Continue searching with D and W.";
    },

    shakeDish() {
      if (
        this.$refs.sketch &&
        typeof this.$refs.sketch.shakeDish === "function"
      ) {
        this.$refs.sketch.shakeDish(1);
        this.onInteraction();
        this.announceLiveRegion("Dish shaken.");
      }
    },

    toggleAdaptiveQuality() {
      this.adaptiveQuality = !this.adaptiveQuality;
      this.announceLiveRegion(
        this.adaptiveQuality
          ? "Adaptive quality enabled."
          : "Adaptive quality disabled.",
        false
      );
    },

    openPerformanceSettings() {
      this.showPerformanceSettings = true;
      this.$nextTick(() => {
        const closeBtn = this.$refs.performanceSettingsCloseBtn;
        const modal = this.$refs.performanceSettingsModal;
        if (closeBtn && typeof closeBtn.focus === "function") {
          closeBtn.focus();
          return;
        }
        if (modal && typeof modal.focus === "function") {
          modal.focus();
        }
      });
    },

    closePerformanceSettings() {
      this.showPerformanceSettings = false;
      this.$nextTick(() => {
        const trigger = this.$refs.performanceSettingsTrigger;
        if (trigger && typeof trigger.focus === "function") {
          trigger.focus();
        }
      });
    },

    cycleCpuTarget() {
      const levels = [0, 30, 45, 60, 75, 90];
      const i = levels.indexOf(this.cpuTarget);
      const next = i === -1 ? 0 : (i + 1) % levels.length;
      this.cpuTarget = levels[next];
      this.announceLiveRegion(
        `CPU target set to ${this.cpuTarget} percent.`,
        false
      );
    },

    onSpecimenCollected(count) {
      this.isPipettePrimed = false;
      this.targetedEmbryoId = "";
      this.collectedCount = count;
      this.announceLiveRegion(`Sow oocyte ${count} of 5 collected!`);
      this.guidedSummary = `Collected ${count} of 5 sow oocytes. Prime again for the next collection.`;
      setTimeout(() => {
        this.focusNextGuidedTarget(false);
      }, 120);
    },

    onAllCollected() {
      this.completed = true;
      this.targetedEmbryoId = "";
      this.announceLiveRegion(
        "All 5 sow oocytes collected! Activity complete!",
        true,
        "assertive"
      );
      this.guidedSummary = "Search complete. All sow oocytes collected.";
      this.guidedHotspotId = "";
      this.setSowOocyteCollection({
        completed: true,
        completedAt: Date.now()
      });
      try {
        this.updateEntry(this.mapToDatastore);
      } catch (_) {
        // Datastore or auth may be disabled in some deployments.
      }
    },

    onInteraction() {
      this.totalInteractions++;
    },

    onBlocked(embryoId) {
      this.targetedEmbryoId = embryoId;
      this.blockedMessage = true;
      clearTimeout(this.blockedTimer);
      this.blockedTimer = setTimeout(() => {
        this.blockedMessage = false;
      }, 2800);

      const suggestion =
        this.$refs.sketch &&
        typeof this.$refs.sketch.getUnblockingTarget === "function"
          ? this.$refs.sketch.getUnblockingTarget(embryoId)
          : null;

      if (suggestion) {
        this.guidedHotspotId = suggestion.id;
        this.guidedSummary =
          "Target is blocked. Move nearby debris, bubble, or particle first.";
        this.announceLiveRegion(
          "Oocyte blocked. Suggested next action: move nearby debris before collecting.",
          true,
          "assertive"
        );
        this.$refs.sketch.focusHotspot(suggestion.id);
      } else {
        this.announceLiveRegion(
          "Oocyte blocked. Move nearby debris, then try collecting again.",
          true,
          "assertive"
        );
      }
    },

    onNeedsPrime() {
      this.needsPrimeMessage = true;
      clearTimeout(this.needsPrimeTimer);
      this.needsPrimeTimer = setTimeout(() => {
        this.needsPrimeMessage = false;
      }, 2800);
      this.announceLiveRegion(
        "Pipette is not primed. Prime pipette before collecting an oocyte.",
        true,
        "assertive"
      );
    },

    initializeGuidedSearch() {
      this.$nextTick(() => {
        this.focusNextGuidedTarget(false, false);
      });
    },

    getGuidedTargets() {
      if (
        !this.$refs.sketch ||
        typeof this.$refs.sketch.getGuidedTargets !== "function"
      ) {
        return [];
      }
      return this.$refs.sketch.getGuidedTargets(this.searchPattern);
    },

    describePattern() {
      return this.searchPattern === "sector-scan"
        ? "Sector scan"
        : "Expanding square";
    },

    announceGuidedTarget(target) {
      const typeName = target.type === "embryo" ? "oocyte" : target.type;
      const visibilityHint =
        target.type === "embryo"
          ? target.obscured
            ? "partially hidden"
            : "visible"
          : "movable";

      this.announceLiveRegion(
        `${this.describePattern()} target: ${typeName}, ring ${target.ring + 1}, ` +
          `sector ${target.sector + 1}, ${visibilityHint}.`
      );
      this.guidedSummary = `${this.describePattern()} target: ${typeName} in ring ${target.ring + 1}, sector ${target.sector + 1}.`;
    },

    focusNextGuidedTarget(announce = true, advance = true) {
      this.targetedEmbryoId = "";
      const targets = this.getNavigableTargets();
      if (!targets.length) {
        this.guidedHotspotId = "";
        this.guidedTargetOrderIds = [];
        this.guidedTargetOrderIndex = -1;
        this.guidedSummary = "No targets available.";
        return;
      }

      this.syncGuidedTargetOrder(targets);

      if (this.guidedHotspotId) {
        const existingIndex = this.guidedTargetOrderIds.indexOf(
          this.guidedHotspotId
        );
        if (existingIndex >= 0) {
          this.guidedTargetOrderIndex = existingIndex;
        }
      }

      if (this.guidedTargetOrderIndex < 0) {
        this.guidedTargetOrderIndex = 0;
      }

      if (advance) {
        this.guidedTargetOrderIndex =
          (this.guidedTargetOrderIndex + 1) % this.guidedTargetOrderIds.length;
      }

      const nextId = this.guidedTargetOrderIds[this.guidedTargetOrderIndex];
      const nextTarget =
        targets.find((target) => target.id === nextId) || targets[0];

      this.guidedHotspotId = nextTarget.id;
      this.guidedTargetOrderIndex = this.guidedTargetOrderIds.indexOf(
        nextTarget.id
      );
      if (announce) {
        this.announceGuidedTarget(nextTarget);
      }
      this.$refs.sketch.focusHotspot(nextTarget.id);
    },

    focusPreviousGuidedTarget() {
      this.targetedEmbryoId = "";
      const targets = this.getNavigableTargets();
      if (!targets.length) {
        this.guidedHotspotId = "";
        this.guidedTargetOrderIds = [];
        this.guidedTargetOrderIndex = -1;
        this.guidedSummary = "No targets available.";
        return;
      }

      this.syncGuidedTargetOrder(targets);

      if (this.guidedHotspotId) {
        const existingIndex = this.guidedTargetOrderIds.indexOf(
          this.guidedHotspotId
        );
        if (existingIndex >= 0) {
          this.guidedTargetOrderIndex = existingIndex;
        }
      }

      if (this.guidedTargetOrderIndex < 0) {
        this.guidedTargetOrderIndex = 0;
      }

      this.guidedTargetOrderIndex =
        (this.guidedTargetOrderIndex - 1 + this.guidedTargetOrderIds.length) %
        this.guidedTargetOrderIds.length;

      const prevId = this.guidedTargetOrderIds[this.guidedTargetOrderIndex];
      const prevTarget =
        targets.find((target) => target.id === prevId) || targets[0];
      this.guidedHotspotId = prevTarget.id;
      this.guidedTargetOrderIndex = this.guidedTargetOrderIds.indexOf(
        prevTarget.id
      );
      this.announceGuidedTarget(prevTarget);
      this.$refs.sketch.focusHotspot(prevTarget.id);
    },

    repeatGuidedHint() {
      const targets = this.getNavigableTargets();
      if (!targets.length) {
        this.announceLiveRegion("No guided targets available.");
        return;
      }

      const target =
        targets.find((t) => t.id === this.guidedHotspotId) || targets[0];
      this.guidedHotspotId = target.id;
      this.announceGuidedTarget(target);
    },

    focusCurrentTarget() {
      const targets = this.getNavigableTargets();
      if (!targets.length) {
        this.announceLiveRegion("No guided targets available.");
        return;
      }

      const firstTarget = targets[0];
      this.targetedEmbryoId = "";
      this.showKeyboardGuidedOutline = true;
      this.syncGuidedTargetOrder(targets);
      this.guidedTargetOrderIndex = 0;
      this.guidedHotspotId = firstTarget.id;
      if (
        this.$refs.sketch &&
        typeof this.$refs.sketch.focusHotspot === "function"
      ) {
        this.$refs.sketch.focusHotspot(firstTarget.id);
      }
      this.announceLiveRegion(
        "Focused first search item. Press W to check for a nearby oocyte."
      );
      this.guidedSummary =
        "Focused first item. Press W to check this area, then D for next.";
    },

    onPatternChanged(event) {
      this.searchPattern = event.target.value;
      this.guidedHotspotId = "";
      this.targetedEmbryoId = "";
      this.guidedTargetOrderIds = [];
      this.guidedTargetOrderIndex = -1;
      this.announceLiveRegion(`${this.describePattern()} selected.`, false);
      this.focusNextGuidedTarget(true, false);
    },

    resetActivity() {
      this.closePerformanceSettings();
      if (import.meta.env.DEV) {
        seedRNG(config.settings.randomSeed);
      }
      this.sketchKey++;
      this.collectedCount = 0;
      this.totalInteractions = 0;
      this.completed = false;
      this.isPipettePrimed = false;
      this.liveRegionText = "";
      this.warningLiveRegionText = "";
      this.shortcutAnnouncementContext = { text: "", at: 0 };
      this.guidedHotspotId = "";
      this.targetedEmbryoId = "";
      this.guidedTargetOrderIds = [];
      this.guidedTargetOrderIndex = -1;
      this.guidedSummary =
        "Press D to cycle through items, then press W to check for oocytes.";
      this.blockedMessage = false;
      this.needsPrimeMessage = false;
      clearTimeout(this.needsPrimeTimer);
      clearTimeout(this.srInterruptTimer);
      clearTimeout(this.srWarningInterruptTimer);
      this.$nextTick(() => {
        if (this.guideMode === "keyboard") {
          this.initializeGuidedSearch();
          return;
        }
        this.setGuideMode("mouse");
      });
    }
  }
};
</script>

<style scoped>
.embryo-rig {
  border-radius: 1.75rem;
  background: linear-gradient(160deg, #f6f7f9 0%, #e9edf2 100%);
  box-shadow:
    0 16px 32px rgba(15, 23, 42, 0.08),
    0 2px 6px rgba(15, 23, 42, 0.06);
}

.embryo-rig__grid {
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
  gap: 1rem;
}

.embryo-stage {
  border-radius: 1.5rem;
  background: radial-gradient(
      circle at 30% 25%,
      rgba(255, 255, 255, 0.04),
      transparent 58%
    ),
    linear-gradient(165deg, #0f1014 0%, #050608 100%);
  min-height: 640px;
  display: block;
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.08),
    0 12px 20px rgba(2, 4, 10, 0.3);
}

.embryo-stage__scope-wrap {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 520px;
}

.embryo-stage__workspace {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.embryo-stage__feedback {
  height: 4.75rem;
  border-radius: 0.8rem;
  padding: 0.55rem 0.8rem;
  margin-bottom: 0.7rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.28);
}

.embryo-stage__feedback--keyboard {
  background: rgba(37, 99, 235, 0.22);
  box-shadow:
    inset 0 0 0 1px rgba(147, 197, 253, 0.32),
    0 4px 10px rgba(2, 6, 23, 0.22);
}

.embryo-stage__feedback--keyboard-near {
  background: rgba(22, 163, 74, 0.24);
  box-shadow:
    inset 0 0 0 1px rgba(74, 222, 128, 0.36),
    0 4px 10px rgba(2, 6, 23, 0.22);
}

.embryo-stage__feedback--mouse {
  background: rgba(217, 119, 6, 0.18);
  box-shadow:
    inset 0 0 0 1px rgba(251, 191, 36, 0.28),
    0 4px 10px rgba(2, 6, 23, 0.22);
}

.embryo-stage__feedback-label {
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(248, 250, 252, 0.82);
  margin-bottom: 0.12rem;
}

.embryo-stage__feedback-text {
  font-size: 0.98rem;
  color: #f8fafc;
  line-height: 1.28;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.embryo-stage__action-btn {
  background-color: #d73f09;
  border: 1px solid #b73507;
  color: #fff;
  box-shadow: 0 10px 16px rgba(120, 53, 15, 0.26);
}

.embryo-stage__action-btn:hover,
.embryo-stage__action-btn:focus {
  background-color: #bf3708;
  border-color: #a62f06;
  color: #fff;
}

.embryo-stage__action-btn:focus-visible {
  box-shadow:
    0 0 0 0.25rem rgba(215, 63, 9, 0.35),
    0 10px 16px rgba(120, 53, 15, 0.26);
}

.embryo-stage__action-btn:active {
  background-color: #a62f06;
  border-color: #8f2805;
  color: #fff;
}

.embryo-stage__action-btn-outline {
  background-color: transparent;
  border: 1px solid #d73f09;
  color: #f5a27f;
  box-shadow: 0 10px 16px rgba(120, 53, 15, 0.2);
}

.embryo-stage__action-btn-outline:hover,
.embryo-stage__action-btn-outline:focus {
  background-color: rgba(215, 63, 9, 0.2);
  border-color: #f07a4c;
  color: #fff;
}

.embryo-stage__action-btn-outline:focus-visible {
  box-shadow:
    0 0 0 0.25rem rgba(215, 63, 9, 0.28),
    0 10px 16px rgba(120, 53, 15, 0.2);
}

.embryo-side-tools {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.embryo-side-tools--two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.embryo-side-tools .btn {
  width: 100%;
  border: 1px solid rgba(17, 24, 39, 0.12);
  box-shadow: 0 8px 14px rgba(17, 24, 39, 0.16);
}

.embryo-side-tools__btn {
  min-height: 52px;
  padding-top: 0.4rem;
  padding-bottom: 0.4rem;
  font-size: 0.94rem;
  line-height: 1.2;
}

.embryo-side-tools__reset-ready {
  font-weight: 600;
  border-color: #157347 !important;
  box-shadow:
    0 0 0 1px rgba(25, 135, 84, 0.22),
    0 10px 18px rgba(25, 135, 84, 0.28) !important;
}

.embryo-status {
  border-radius: 1rem;
  background: linear-gradient(180deg, #f4f6f9 0%, #eceff3 100%);
  min-height: 640px;
  box-shadow:
    inset 0 0 0 1px rgba(148, 163, 184, 0.35),
    0 8px 18px rgba(30, 41, 59, 0.12);
}

.embryo-status__body {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.embryo-status__title {
  font-size: clamp(1.15rem, 1.9vw, 1.85rem);
  line-height: 1.05;
}

.embryo-progress {
  background: linear-gradient(180deg, #ffffff 0%, #f7f9fc 100%);
  box-shadow:
    inset 0 0 0 1px rgba(14, 116, 144, 0.18),
    0 8px 16px rgba(14, 116, 144, 0.12) !important;
}

.embryo-progress .embryo-status__title {
  font-size: clamp(1.25rem, 2.15vw, 2rem);
}

.embryo-guide {
  background: rgba(255, 255, 255, 0.66);
  box-shadow: none !important;
  border: 1px dashed rgba(100, 116, 139, 0.45);
}

.embryo-guide__body {
  min-height: 19.2rem;
}

.embryo-instructions :deep(.alert) {
  padding: 1rem 1.2rem;
}

.embryo-instructions :deep(.alert p),
.embryo-instructions :deep(.alert ol) {
  margin-bottom: 0.9rem;
}

.embryo-instructions :deep(.alert ol) {
  padding-left: 1.25rem;
}

.embryo-instructions :deep(.alert li + li) {
  margin-top: 0.45rem;
}

.embryo-keyboard-guidance {
  border-top: 1px solid rgba(13, 110, 253, 0.25);
  border-bottom: 1px solid rgba(13, 110, 253, 0.25);
  padding-top: 0.65rem;
  padding-bottom: 0.65rem;
}

.embryo-keyboard-guidance__panel {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(13, 110, 253, 0.32);
  border-radius: 0.5rem;
  padding: 0.8rem 0.9rem;
}

.embryo-keyboard-guidance__panel :deep(p) {
  font-size: 0.95rem;
  line-height: 1.45;
}

.embryo-keyboard-guidance__list li + li {
  margin-top: 0.5rem;
  padding-top: 0.45rem;
  border-top: 1px dashed rgba(100, 116, 139, 0.32);
}

.embryo-keyboard-guidance__list li {
  font-size: 1rem;
  line-height: 1.5;
}

.embryo-keyboard-guidance__list :deep(kbd) {
  min-width: 2rem;
  padding: 0.15rem 0.4rem;
  text-align: center;
}

.embryo-keyboard-guidance__keys {
  align-items: center;
  white-space: nowrap;
}

.embryo-keyboard-guidance__keys .fw-semibold {
  font-size: 0.78rem;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  color: #334155;
}

.embryo-key-legend :deep(kbd) {
  min-width: 1.75rem;
  padding: 0.15rem 0.45rem;
  text-align: center;
}

.embryo-key-legend :deep(.border) {
  min-height: 2.2rem;
}

.embryo-tray {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.4rem;
}

.embryo-tray__slot {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  border: 2px dashed #b38d59;
  background: #d8b47a;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color 0.3s,
    background 0.3s;
}

.embryo-tray__slot--filled {
  border-color: #886c47;
  background: #ecc27a;
  border-style: solid;
}

@keyframes embryo-pop {
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  70% {
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.embryo-tray__slot--animating .embryo-tray__embryo {
  animation: embryo-pop 0.35s ease-out;
}

.embryo-tray__embryo {
  position: relative;
  width: 70%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.embryo-tray__outer-shell {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 1.5px solid rgba(136, 108, 71, 0.65);
}

.embryo-tray__inner-core {
  position: relative;
  width: 78%;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  background: radial-gradient(circle at 38% 38%, #ecc27a, #c79a61 58%, #886c47);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 3px;
}

.embryo-tray__cap {
  width: 14px;
  height: 8px;
  border-radius: 50%;
  background: rgba(236, 194, 122, 0.85);
}

@media (max-width: 1199.98px) {
  .embryo-rig__grid {
    grid-template-columns: 1fr;
  }

  .embryo-stage,
  .embryo-status {
    min-height: auto;
  }

  .embryo-stage__scope-wrap {
    min-height: auto;
  }

  .embryo-stage__workspace {
    width: 100%;
  }

  .embryo-side-tools {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
  }

  .embryo-guide__body {
    min-height: 0;
  }
}
</style>
